import { useEffect, useRef, useState } from "react";
import { Icon } from "../Common.jsx";
import { fmtDate } from "../../lib/dates.js";
import { formatMoney } from "../../lib/money.js";
import { getInvoice } from "../../lib/adminApi";

const STATUS_LABEL = {
  paid: "Paid",
  unpaid: "Unpaid",
  voided: "Voided",
  partially_refunded: "Partially Refunded",
  refunded: "Refunded",
};



function InvoiceSkeleton() {
  return (
    <div style={{ padding: 48, textAlign: "center", color: "var(--text-muted)" }}>
      Loading invoice…
    </div>
  );
}

export default function InvoiceDetailModal({ invoiceId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getInvoice(invoiceId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message || "Load failed"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [invoiceId]);

  const currency = data?.currency || "INR";
  const refundedAmount = data?.refundedAmount ?? 0;
  const company = data?.company;
  const subTotal = data?.amount || 0;
  const totalPaid = refundedAmount > 0 ? subTotal - refundedAmount : subTotal;

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const styles = Array.from(document.styleSheets)
      .map((s) => {
        try {
          return Array.from(s.cssRules || []).map((r) => r.cssText).join("\n");
        } catch { return ""; }
      })
      .join("\n");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head><title>Invoice ${data?.id?.slice(0, 8) || ""}</title>
      <style>
        ${styles}
        @page { margin: 15mm; }
        body {
          font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
          background: #fff;
          color: #1a1a1a;
          margin: 0;
          padding: 40px;
          font-size: 13px;
          line-height: 1.5;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .inv-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 24px; border-bottom: 2px solid #1a1a1a; }
        .inv-brand { display: flex; flex-direction: column; gap: 4px; }
        .inv-brand-name { font-size: 22px; font-weight: 800; letter-spacing: -0.5px; color: #1a1a1a; }
        .inv-brand-sub { font-size: 11px; color: #666; }
        .inv-badge { display: inline-block; padding: 4px 14px; border-radius: 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
        .inv-badge-paid { background: #d1fae5; color: #065f46; }
        .inv-badge-unpaid { background: #fef3c7; color: #92400e; }
        .inv-badge-voided { background: #f3f4f6; color: #6b7280; }
        .inv-badge-refunded, .inv-badge-partially_refunded { background: #dbeafe; color: #1e40af; }
        .inv-badge-default { background: #f3f4f6; color: #6b7280; }
        .inv-title { font-size: 28px; font-weight: 300; text-align: right; letter-spacing: 3px; color: #1a1a1a; margin: 0; }
        .inv-meta { text-align: right; font-size: 11px; color: #666; margin-top: 4px; }
        .inv-info { display: flex; justify-content: space-between; margin-bottom: 36px; }
        .inv-box { }
        .inv-box-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #999; margin-bottom: 4px; }
        .inv-box-value { font-size: 14px; font-weight: 600; color: #1a1a1a; }
        .inv-box-sub { font-size: 12px; color: #666; margin-top: 2px; }
        .inv-table { width: 100%; border-collapse: collapse; margin-bottom: 32px; }
        .inv-table th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #999; padding: 10px 12px; border-bottom: 1px solid #e5e7eb; }
        .inv-table td { padding: 14px 12px; border-bottom: 1px solid #f3f4f6; font-size: 13px; }
        .inv-table td:last-child, .inv-table th:last-child { text-align: right; }
        .inv-table td:nth-child(2), .inv-table th:nth-child(2) { text-align: right; }
        .inv-total-wrap { margin-left: auto; width: 280px; }
        .inv-total-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; }
        .inv-total-row:last-child { border-top: 2px solid #1a1a1a; padding-top: 10px; margin-top: 4px; font-weight: 700; font-size: 16px; }
        .inv-total-label { color: #666; }
        .inv-total-value { font-weight: 600; }
        .inv-footer { margin-top: 48px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #999; text-align: center; }
        .no-print { display: none !important; }
        .inv-refunds { margin-top: 32px; }
        .inv-refunds h4 { font-size: 12px; text-transform: uppercase; letter-spacing: 1px; color: #999; margin-bottom: 8px; }
      </style></head>
      <body>
        <div class="inv-header">
          <div class="inv-brand">
            <div class="inv-brand-name">Bizzfly</div>
            <div class="inv-brand-sub">Daily Reports Platform</div>
          </div>
          <div>
            <h1 class="inv-title">INVOICE</h1>
            <div class="inv-meta">${data.id}</div>
          </div>
        </div>

        <div class="inv-info">
          <div class="inv-box">
            <div class="inv-box-label">Bill To</div>
            <div class="inv-box-value">${company?.name || "—"}</div>
            <div class="inv-box-sub">${company?.slug || ""}</div>
          </div>
          <div class="inv-box" style="text-align:right">
            <div class="inv-box-label">Invoice Date</div>
            <div class="inv-box-value">${fmtDate(data.createdAt)}</div>
            <div class="inv-box-sub">Period: ${fmtDate(data.periodStart)} — ${fmtDate(data.periodEnd)}</div>
          </div>
        </div>

        <table class="inv-table">
          <thead>
            <tr>
              <th style="width:60%">Description</th>
              <th style="width:20%">Rate</th>
              <th style="width:20%">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Subscription — ${fmtDate(data.periodStart)} to ${fmtDate(data.periodEnd)}</td>
              <td>${formatMoney(subTotal, currency)}</td>
              <td>${formatMoney(subTotal, currency)}</td>
            </tr>
          </tbody>
        </table>

        <div class="inv-total-wrap">
          <div class="inv-total-row">
            <span class="inv-total-label">Subtotal</span>
            <span>${formatMoney(subTotal, currency)}</span>
          </div>
          ${refundedAmount > 0 ? `
          <div class="inv-total-row">
            <span class="inv-total-label">Refunded</span>
            <span style="color:#dc2626">-${formatMoney(refundedAmount, currency)}</span>
          </div>
          ` : ""}
          <div class="inv-total-row">
            <span class="inv-total-label">Total</span>
            <span>${formatMoney(totalPaid, currency)}</span>
          </div>
        </div>

        ${(data.refunds || []).length > 0 ? `
        <div class="inv-refunds">
          <h4>Refunds</h4>
          <table class="inv-table">
            <thead><tr><th>Amount</th><th>Status</th><th>Reason</th><th>Date</th></tr></thead>
            <tbody>
              ${data.refunds.map((r) => `
              <tr>
                <td>${formatMoney(r.amount, currency)}</td>
                <td><span class="inv-badge ${r.status === "processed" ? "inv-badge-paid" : r.status === "failed" ? "" : "inv-badge-default"}">${r.status}</span></td>
                <td>${r.reason || "—"}</td>
                <td>${fmtDate(r.createdAt)}</td>
              </tr>`).join("")}
            </tbody>
          </table>
        </div>
        ` : ""}

        <div class="inv-footer">
          Bizzfly · Daily Reports Platform · Invoice ${data.id.slice(0, 8)}
        </div>
        <script>window.print();</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <>
        <div className="modal-head">
          <h3><Icon name="receipt" size={16} /> Invoice</h3>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body"><InvoiceSkeleton /></div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="modal-head">
          <h3><Icon name="receipt" size={16} /> Invoice</h3>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ padding: 24, textAlign: "center" }}>
            <Icon name="alert-triangle" size={32} style={{ color: "var(--error)", marginBottom: 8 }} />
            <p style={{ color: "var(--error)", fontSize: 13 }}>{error}</p>
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </>
    );
  }

  if (!data) return null;

  return (
    <>
      <div className="modal-head">
        <h3><Icon name="receipt" size={16} /> Invoice</h3>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="btn btn-ghost btn-sm" onClick={handlePrint}>
            <Icon name="printer" size={14} /> Print
          </button>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
      </div>

      <div className="modal-body" ref={printRef} style={{ padding: 0 }}>
        {/* Invoice document */}
        <div className="invoice-doc" style={{
          background: "#fff",
          color: "#1a1a1a",
          fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
          fontSize: 13,
          lineHeight: 1.5,
        }}>
          {/* Header */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "flex-start",
            padding: "32px 32px 20px",
            borderBottom: "2px solid #1a1a1a",
            marginBottom: 24,
          }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.5px", color: "#1a1a1a" }}>
                Bizzfly
              </div>
              <div style={{ fontSize: 11, color: "#666" }}>Daily Reports Platform</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 28, fontWeight: 300, letterSpacing: 3, color: "#1a1a1a", margin: 0 }}>
                INVOICE
              </div>
              <div style={{ fontSize: 11, color: "#666", marginTop: 4, fontFamily: "monospace" }}>
                {data.id}
              </div>
            </div>
          </div>

          {/* Status badge */}
          <div style={{ padding: "0 32px", marginBottom: 20 }}>
            <span style={{
              display: "inline-block", padding: "3px 12px", borderRadius: 20,
              fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1,
              background: data.status === "paid" ? "#d1fae5" :
                data.status === "unpaid" ? "#fef3c7" :
                data.status === "voided" ? "#f3f4f6" :
                "#dbeafe",
              color: data.status === "paid" ? "#065f46" :
                data.status === "unpaid" ? "#92400e" :
                data.status === "voided" ? "#6b7280" :
                "#1e40af",
            }}>
              {STATUS_LABEL[data.status] || data.status}
            </span>
          </div>

          {/* Two-column info */}
          <div style={{
            display: "flex", justifyContent: "space-between",
            padding: "0 32px", marginBottom: 28,
          }}>
            <div>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "#999", marginBottom: 4 }}>
                Bill To
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>
                {company?.name || "—"}
              </div>
              {company?.slug && (
                <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{company.slug}</div>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "#999", marginBottom: 4 }}>
                Invoice Details
              </div>
              <div style={{ fontSize: 13, color: "#1a1a1a" }}>
                Date: {fmtDate(data.createdAt)}
              </div>
              <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                Period: {fmtDate(data.periodStart)} — {fmtDate(data.periodEnd)}
              </div>
              {data.razorpayInvoiceId && (
                <div style={{ fontSize: 11, color: "#999", marginTop: 2, fontFamily: "monospace" }}>
                  Ref: {data.razorpayInvoiceId}
                </div>
              )}
            </div>
          </div>

          {/* Line items table */}
          <div style={{ padding: "0 32px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{
                    textAlign: "left", fontSize: 10, textTransform: "uppercase",
                    letterSpacing: 1, color: "#999", padding: "10px 12px",
                    borderBottom: "1px solid #e5e7eb",
                  }}>Description</th>
                  <th style={{
                    textAlign: "right", fontSize: 10, textTransform: "uppercase",
                    letterSpacing: 1, color: "#999", padding: "10px 12px",
                    borderBottom: "1px solid #e5e7eb", width: 120,
                  }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: "14px 12px", borderBottom: "1px solid #f3f4f6", fontSize: 13 }}>
                    <div style={{ fontWeight: 500 }}>Subscription — {fmtDate(data.periodStart)} to {fmtDate(data.periodEnd)}</div>
                  </td>
                  <td style={{
                    padding: "14px 12px", borderBottom: "1px solid #f3f4f6",
                    fontSize: 13, textAlign: "right", fontWeight: 600,
                  }}>
                    {formatMoney(subTotal, currency)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div style={{ padding: "0 32px", marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
            <div style={{ width: 260 }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 13 }}>
                <span style={{ color: "#666" }}>Subtotal</span>
                <span style={{ fontWeight: 600 }}>{formatMoney(subTotal, currency)}</span>
              </div>
              {refundedAmount > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 13 }}>
                  <span style={{ color: "#666" }}>Refunded</span>
                  <span style={{ color: "#dc2626", fontWeight: 600 }}>-{formatMoney(refundedAmount, currency)}</span>
                </div>
              )}
              <div style={{
                display: "flex", justifyContent: "space-between",
                padding: "10px 0 0", marginTop: 4,
                borderTop: "2px solid #1a1a1a",
                fontSize: 16, fontWeight: 700,
              }}>
                <span>Total</span>
                <span>{formatMoney(totalPaid, currency)}</span>
              </div>
            </div>
          </div>

          {/* Refunds table */}
          {(data.refunds || []).length > 0 && (
            <div style={{ padding: "24px 32px 0" }}>
              <h4 style={{
                fontSize: 12, textTransform: "uppercase", letterSpacing: 1,
                color: "#999", marginBottom: 8,
              }}>Refunds</h4>
              <div className="table-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Reason</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.refunds.map((r) => (
                      <tr key={r.id}>
                        <td><strong>{formatMoney(r.amount, currency)}</strong></td>
                        <td>
                          <span className={`badge ${r.status === "processed" ? "badge-success" : r.status === "failed" ? "badge-error" : "badge-warning"}`}>
                            {r.status}
                          </span>
                        </td>
                        <td style={{ fontSize: 12 }}>{r.reason || "—"}</td>
                        <td style={{ fontSize: 12, color: "var(--text-muted)" }}>{fmtDate(r.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={{
            marginTop: 40, padding: "16px 32px 24px",
            borderTop: "1px solid #e5e7eb",
            fontSize: 10, color: "#999", textAlign: "center",
          }}>
            Bizzfly &middot; Daily Reports Platform &middot; Invoice {data.id.slice(0, 8)}
          </div>
        </div>
      </div>

      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose}>Close</button>
        <button className="btn btn-primary" onClick={handlePrint}>
          <Icon name="printer" size={14} /> Print Invoice
        </button>
      </div>
    </>
  );
}
